import find from 'lodash/find';
import { Paths } from 'type-fest';

import {
  AwardVouchersFragment,
  EarningMechanismFragment,
  MarketingContentFragment,
  OfferFragment,
} from '@occc/graphql';
import { isOfferFragment } from '@occc-app/offerValidationService/utils';
import { getEarningMechanismAward } from '@occc-app/offerHelpers/utils/getEarningMechanismAward';
import { offerValidationService } from '@occc-app/offerValidationService';
import {
  OfferType,
  PartialOfferFragment,
  PartialOfferSchema,
} from '@occc-app/offerValidationService/types';
import { isEmptyCustomDataString } from '@occc-app/routes/CampaignComponents/components/CustomData/utils';
import { CustomDataFormValuesYup } from '@occc-app/routes/CampaignComponents/components/CustomData/types';
import { CustomDataFormValues } from '@occc/validation';

import { earningMechanismHelper } from './utils/earningMechanismHelper';
import { awardHelper } from './utils/awardHelper';
import { DirtyFieldsType } from './types';

import { parseDate } from '../lib/date';

// Type-safe version for static paths that can be validated at compile time
// Use this when path is known at development time
export function isFieldDirty<T = any>(
  dirtyFields: DirtyFieldsType<T> | undefined,
  path: Paths<T>,
): boolean {
  if (!dirtyFields) {
    return false;
  }

  return !!find(dirtyFields, (dirty, name) => name.startsWith(path as string) && dirty);
}

// Version for string paths that can't be validated at compile time
// Use this when path is dynamically constructed (e.g., using template literals)
// or when working with paths that depend on runtime values
export function isFieldWithPathDirty<T = any>(
  dirtyFields: DirtyFieldsType<T> | undefined,
  path: string,
): boolean {
  if (!dirtyFields) {
    return false;
  }

  return !!find(dirtyFields, (dirty, name) => name.startsWith(path as string) && dirty);
}

export const getOfferComponentType = (offer: OfferFragment | PartialOfferFragment) => {
  let offerSchema: PartialOfferSchema | null;
  if (isOfferFragment(offer)) {
    offerSchema = offerValidationService.getTypeFromOfferData(offer);
  } else {
    offerSchema = offerValidationService.getTypeFromPartialOfferData(offer);
  }
  return offerSchema ? offerSchema.type : null;
};

export const getOfferComponentSubType = (offer: OfferFragment | PartialOfferFragment) => {
  let offerSchema: PartialOfferSchema | null;
  if (isOfferFragment(offer)) {
    offerSchema = offerValidationService.getTypeFromOfferData(offer);
  } else {
    offerSchema = offerValidationService.getTypeFromPartialOfferData(offer);
  }
  return offerSchema ? offerSchema.subtype : null;
};

export const isOfferActive = (
  publishAt: string | null | undefined,
  expireAt: string | null | undefined,
) => {
  const now = Date.now();
  const start = publishAt ? parseDate(publishAt).valueOf() : now;
  const end = expireAt ? parseDate(expireAt).valueOf() : now;

  const isAfterPublication = now > start;
  const isBeforeExpiration = now < end;

  return isAfterPublication && (isBeforeExpiration || !end);
};

export const getVoucherAward = (
  earningMechanism?: EarningMechanismFragment | null,
): AwardVouchersFragment | null => {
  const award = earningMechanism ? getEarningMechanismAward(earningMechanism) : null;
  return award && awardHelper.isVouchersAward(award) ? award : null;
};

const titleByType: { [key in OfferType]: string } = {
  [OfferType.FuelRollback]: 'Fuel Rollback',
  [OfferType.ClubOrPunchcard]: 'Club / Punchcard',
  [OfferType.Challenge]: 'Challenge',
  [OfferType.Reward]: 'Reward',
  [OfferType.Promotion]: 'Promotion',
  [OfferType.Offer]: 'Offer',
  [OfferType.Sweepstakes]: 'Sweepstakes',
  [OfferType.Award]: 'Award',
  [OfferType.Billboard]: 'Billboard',
  [OfferType.LoyaltyEvent]: 'Loyalty Event Offer',
  [OfferType.StoredValue]: 'Stored Value Program',
  [OfferType.FreeGame]: 'Game',
  [OfferType.VoucheredGame]: 'Vouchered Game',
  [OfferType.ExternalFulfillment]: 'External',
  [OfferType.Promocodes]: 'Promo Code Offer',
};

export const getTitleByOfferType = (
  type: OfferType | null | undefined,
  earningMechanism?: EarningMechanismFragment | null,
): string => {
  if (
    type === OfferType.Offer &&
    earningMechanism &&
    earningMechanismHelper.isExternalFulfillment(earningMechanism)
  ) {
    return titleByType[OfferType.ExternalFulfillment];
  }

  return type ? titleByType[type] : '';
};

export const hasMarketingContent = (offer: OfferFragment): boolean => {
  return offer.marketingContents?.length > 0;
};

export const getCustomDataFromFormValues = (values: CustomDataFormValues | undefined) => {
  const customDataString = typeof values?.data === 'string' ? values.data : undefined;

  const isCustomDataEmpty =
    !values?.isEnabled || !customDataString || isEmptyCustomDataString(customDataString);

  return isCustomDataEmpty ? null : JSON.parse(customDataString);
};

// Games still use Yup based validation, so we need to keep this function here.
export const getCustomDataFromFormValuesYup = (values: CustomDataFormValuesYup | undefined) => {
  const customDataString = typeof values?.data === 'string' ? values.data : undefined;

  const isCustomDataEmpty =
    !values?.isEnabled || !customDataString || isEmptyCustomDataString(customDataString);

  return isCustomDataEmpty ? null : JSON.parse(customDataString);
};

export type GetMarketingContentDirtyFieldsValuesParams = {
  dirtyFields: Record<string, any>;
  values: {
    marketingContent: Partial<MarketingContentFragment>;
    [key: string]: any;
  };
};

export const getMarketingContentDirtyFieldsValues = ({
  dirtyFields,
  values,
}: GetMarketingContentDirtyFieldsValuesParams): Partial<MarketingContentFragment> => {
  const dirtyFieldsKeys = Object.keys(dirtyFields || {}).filter((item: string) =>
    item.match('marketingContent'),
  );

  return dirtyFieldsKeys.reduce<Partial<MarketingContentFragment>>((accumulator, currentValue) => {
    const fieldName = currentValue.split('.')[1] as keyof MarketingContentFragment;

    accumulator[fieldName] = (values.marketingContent as any)[fieldName] ?? null;

    return accumulator;
  }, {});
};
